// Catalog.ID Client — `create-chat-address`: the contact-consent ceremony
// ============================================================================
// The last step of the contact-accept ceremony (contact-flow.md §7 step 5, §8)
// and the only writer of the `chat_address` claim. The chat backend supplies the
// VALUE — it is the side that knows the relay operator code — and the peer to
// share with; this tab does the crypto, because claim values are
// client-encrypted before they ever reach the CID server.
//
// The authoritative contract is the XML doc on
// `CrossModuleCanonical.BuildCreateChatAddress`
// (apps/desktop/src/shared/moduleipc/CrossModuleCanonical.cs). Body fields:
// `peerCid`, `chatAddressValue`, `chatAddressDigestBase64`,
// `requiresUserInteraction: true`. Approved shape:
// `{status:"approved", claimUuid, shared:true}`.
//
// LOCK POSTURE — a fourth one. `get-claims` degrades, `decap-chat-epoch` fails
// fast, `issue-cert` waits. This one ASKS FIRST and then waits: it is
// ceremony-grade like `issue-cert` (`requiresUserInteraction: true`, so the
// dashboard refocuses this tab and the 90 s wait is meaningful), but unlike
// `issue-cert` it also carries a consent dialog — see
// `chat-address-consent-modal.ts` for why this mode has one and that one does
// not.
//
// WHY THE UNLOCK GATE COMES BEFORE THE CONSENT DIALOG (reversed by M2c,
// 2026-09-18): asking first used to spare a declining member an unlock they did
// not need — nothing in this dialog is derived from the keybag, the peer, the
// operator and the key fingerprint all come from the (digest-bound) request.
// But a dialog rendered in a tab the member is not looking at is invisible, and
// after M2c the focus-request raised by the unlock gate is the only thing that
// brings this tab to the front. So on a LOCKED bag the member is unlocked
// first and asked second; on an unlocked bag the gate is a no-op and the
// dialog still comes before any keybag work.
//
// Gate stack (in order):
//   1–3. sender stamp / requester / active identity — `dispatchChatRequest`.
//   4.   required fields present.
//   5.   `peerCid` is a well-formed CID and is not this account.
//   6.   body↔canonical binding: SHA-512(utf8(peerCid + "|" + value)) equals the
//        signed `chatAddressDigestBase64`. The canonical arm committed to that
//        digest and the (long) value rides in the body, so without this a
//        relay-verified envelope could be re-aimed at another account or have
//        another reachability record substituted under it.
//   7.   the value is a `chat_address.v1` record over the ACTIVE identity, with
//        the §8 closed field set.
//   8.   UNLOCK — the universal redirect (M2c): a locked bag raises this tab
//        and waits out the shared budget, whatever the sender's flag says.
//        AHEAD of the consent modal, because a modal in an unfocused tab is
//        invisible; a no-op when the bag is already open, which leaves the
//        old consent-first order intact for the ordinary case.
//   9.   CONSENT — a modal naming the peer. Declining is a distinct refusal.
//  10.   `attr_encrypt` is in the bag (the claim key is wrapped to it).
//  11.   create-or-reuse the claim, then share it — both idempotent
//        (`chat-address-store.ts`).

import { sha512 } from '@noble/hashes/sha2.js';
import { keys } from '../state/keys';
import * as aes from '../crypto/aes';
import { ATTR_ENCRYPT_KEY_ID, ClaimShareError } from '../state/claim-sharing';
import type { ChatRequest, ChatResponse, ChatDispatchDeps } from './chat-types';
import { ensureUnlocked } from './unlock-gate';

/** A CID is a fixed-shape 10-character lowercase alphanumeric. */
const CID_PATTERN = /^[a-z0-9]{10}$/;

/** contact-flow.md §8: the record type this mode accepts, and only this one. */
export const CHAT_ADDRESS_RECORD_TYPE = 'chat_address.v1';

/** §8's closed field set. An unknown field means a newer record type, not a v1. */
const CHAT_ADDRESS_FIELDS = new Set([
    'type', 'cid', 'chatEncryptPubkey', 'relayOperatorId', 'updatedAt',
]);

/** contact-flow.md §8: the combined `chat_encrypt` public key is exactly 1600 bytes. */
export const CHAT_ENCRYPT_PUBLIC_KEY_LEN = 1600;

type ResponseBase = Pick<ChatResponse, 'type' | 'to' | 'requestId'>;

/**
 * Digest convention shared with the chat service's canonical builder and the
 * relay's C# mirror (`CrossModuleCanonical.ComputeChatAddressDigest`): base64
 * SHA-512 over the UTF-8 bytes of `peerCid + "|" + chatAddressValue`.
 *
 * ONE digest over BOTH halves because the act being authorised has two
 * inseparable halves — WHICH peer, and WHAT value. The `"|"` separator is
 * unambiguous because a CID is fixed-shape and comes first, so the first `|` is
 * always the separator whatever the JCS value contains.
 */
export function computeChatAddressDigest(peerCid: string, chatAddressValue: string): string {
    return aes.toBase64(sha512(new TextEncoder().encode(`${peerCid}|${chatAddressValue}`)));
}

/** The parsed `chat_address.v1` record, or a stable refusal code. */
export type ChatAddressParse =
    | { ok: true; record: ChatAddressRecord }
    | { ok: false; reason: string };

export interface ChatAddressRecord {
    type: string;
    cid: string;
    chatEncryptPubkey: string;
    relayOperatorId: string;
    updatedAt: string;
}

/**
 * Validate the claim VALUE against contact-flow.md §8 before anything durable
 * happens.
 *
 * Two of these checks are load-bearing rather than hygiene:
 *
 *   * `cid === accountCid` — the requester does not get to name the account its
 *     record is written under. Without it, a chat module talking to the wrong
 *     tab would publish someone else's reachability from this account, and the
 *     consent dialog would truthfully say "sharing as <you>" about a record that
 *     is not yours. Same posture as `issue-cert` binding the cert to the ACTIVE
 *     identity.
 *   * unknown fields refused — §8 calls the field set closed, and the version
 *     lives in `type`. A v2 record must arrive as `chat_address.v2` and be
 *     handled deliberately, not smuggled past a v1 handler that ignores what it
 *     does not recognise.
 */
export function parseChatAddressValue(
    chatAddressValue: string, accountCid: string,
): ChatAddressParse {
    let parsed: unknown;
    try {
        parsed = JSON.parse(chatAddressValue);
    } catch {
        return { ok: false, reason: 'chat-address-not-json' };
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return { ok: false, reason: 'chat-address-not-object' };
    }

    const obj = parsed as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
        if (!CHAT_ADDRESS_FIELDS.has(key)) {
            return { ok: false, reason: 'chat-address-unknown-field' };
        }
    }
    for (const key of CHAT_ADDRESS_FIELDS) {
        if (typeof obj[key] !== 'string' || (obj[key] as string).length === 0) {
            return { ok: false, reason: 'chat-address-missing-field' };
        }
    }

    const record = obj as unknown as ChatAddressRecord;
    if (record.type !== CHAT_ADDRESS_RECORD_TYPE) {
        return { ok: false, reason: 'chat-address-wrong-type' };
    }
    if (record.cid !== accountCid) {
        return { ok: false, reason: 'chat-address-not-own-cid' };
    }

    let pubkey: Uint8Array;
    try {
        pubkey = aes.fromBase64(record.chatEncryptPubkey);
    } catch {
        return { ok: false, reason: 'chat-address-bad-pubkey-base64' };
    }
    if (pubkey.length !== CHAT_ENCRYPT_PUBLIC_KEY_LEN) {
        return { ok: false, reason: 'chat-address-pubkey-wrong-length' };
    }

    // "the owner's relay operator CODE — never a URL" (plan §4). A URL here
    // would be silently stored and later handed to a relay client as an
    // operator id.
    if (/[:/\\]/.test(record.relayOperatorId)) {
        return { ok: false, reason: 'chat-address-operator-not-a-code' };
    }

    return { ok: true, record };
}

function unavailable(base: ResponseBase, reason: string): ChatResponse {
    return { ...base, status: 'unavailable', reason };
}

export async function handleCreateChatAddress(
    request: ChatRequest,
    base: ResponseBase,
    accountCid: string,
    deps: ChatDispatchDeps,
): Promise<ChatResponse> {
    // Gate 4.
    if (!request.peerCid || !request.chatAddressValue || !request.chatAddressDigestBase64) {
        return unavailable(base, 'missing-required-field');
    }

    // Gate 5.
    if (!CID_PATTERN.test(request.peerCid)) {
        return unavailable(base, 'malformed-peer-cid');
    }
    if (request.peerCid === accountCid) {
        // Consent to be messaged BY YOURSELF is not a thing, and the server
        // refuses a share to the owner anyway (the `self` wrap lives in the
        // same table). Refuse it here with a name rather than at the API.
        return unavailable(base, 'peer-is-self');
    }

    // Gate 6: body↔canonical binding.
    if (computeChatAddressDigest(request.peerCid, request.chatAddressValue)
        !== request.chatAddressDigestBase64) {
        return unavailable(base, 'chat-address-digest-mismatch');
    }

    // Gate 7.
    const parsed = parseChatAddressValue(request.chatAddressValue, accountCid);
    if (!parsed.ok) return unavailable(base, parsed.reason);

    // Gate 8: the universal unlock redirect (M2c, §10 item 5), and it runs BEFORE the
    // consent dialog rather than after it: a dialog drawn in a tab the member
    // is not looking at is invisible, and the focus-request is the only thing
    // that makes this tab visible. On an unlocked bag the gate is a no-op and
    // the old consent-first order stands.
    if (!await ensureUnlocked(request, deps)) return unavailable(base, 'cid-locked');

    // Gate 9: consent. This is §8's consent primitive — the share IS the record
    // of it — so it is never implicit.
    if (!deps.confirmChatAddressShare) {
        // Wired in by `key-grant/client.ts`. Absent means a build that cannot
        // ask, and a mode that writes a consent record must never proceed
        // without asking.
        return unavailable(base, 'consent-ui-unavailable');
    }
    const decision = await deps.confirmChatAddressShare({
        owningCid: accountCid,
        peerCid: request.peerCid,
        requesterId: request.requesterId,
        relayOperatorId: parsed.record.relayOperatorId,
        chatEncryptFingerprint: toHexLower(sha512(aes.fromBase64(parsed.record.chatEncryptPubkey))),
    });
    if (decision !== 'approve') {
        return { ...base, status: 'denied', reason: 'chat-address-share-declined' };
    }

    // Gate 10. Every claim symmetric key is wrapped to this slot; without it
    // neither the reuse scan nor the re-wrap can run.
    if (!keys.getPrivateKey(ATTR_ENCRYPT_KEY_ID)) {
        return unavailable(base, 'attr-encrypt-not-in-bag');
    }

    // Gate 11.
    if (!deps.createAndShareChatAddress) {
        return unavailable(base, 'claim-store-unavailable');
    }
    try {
        const result = await deps.createAndShareChatAddress({
            peerCid: request.peerCid,
            chatAddressValue: request.chatAddressValue,
            label: `Chat address @ ${parsed.record.relayOperatorId}`,
        });
        return {
            ...base,
            status: 'approved',
            // Server-assigned, echoed — never predicted (migration §17, D1).
            claimUuid: result.claimUuid,
            shared: result.shared,
        };
    } catch (err) {
        // `ClaimShareError` codes are stable and chat branches on them; anything
        // else is reported by name so a transport failure is not mistaken for a
        // refusal.
        if (err instanceof ClaimShareError) return unavailable(base, err.code);
        const message = err instanceof Error ? err.message.slice(0, 80) : 'chat-address-failed';
        return unavailable(base, message);
    }
}

function toHexLower(bytes: Uint8Array): string {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
        const b = bytes[i] as number;
        out += (b < 16 ? '0' : '') + b.toString(16);
    }
    return out;
}
